import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import QrScanner from '../../components/qr-scanner';
import urlValidationService, { ValidationResult } from '../../services/url-validation-service';

/**
 * Scan Page Component
 *
 * This page provides QR code scanning functionality for the museum app.
 * It allows users to scan QR codes that contain URLs to rooms or exhibits,
 * validates the URLs against a whitelist of trusted domains, and navigates
 * to valid URLs.
 */
const ScanPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isValidating, setIsValidating] = useState<boolean>(false);
    const [isScanning, setIsScanning] = useState<boolean>(false);
    const [isScannerActive, setIsScannerActive] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [scanResult, setScanResult] = useState<string | null>(null);
    const [cameraPermission, setCameraPermission] = useState<boolean | null>(null);

    // Start scanning when the component mounts
    useEffect(() => {
        // Request camera permission
        navigator.mediaDevices.getUserMedia({ video: true })
            .then(() => {
                setCameraPermission(true);
                setIsScanning(true);
                setIsScannerActive(true);
            })
            .catch((err) => {
                console.error('Camera permission denied:', err);
                setCameraPermission(false);
                setError('Camera access denied. Please allow camera access to scan QR codes.');
            });

        // Clean up when component unmounts
        return () => {
            console.log('Scan page unmounting, closing camera');
            setIsScannerActive(false);
            setIsScanning(false);
        };
    }, []);

    // Handle page visibility changes to stop camera when page is hidden
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                console.log('Page hidden, deactivating scanner');
                setIsScannerActive(false);
            } else {
                console.log('Page visible, activating scanner');
                setIsScannerActive(true);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    // Handle navigation away from the page
    useEffect(() => {
        const handleBeforeUnload = () => {
            console.log('Page unloading, closing camera');
            setIsScannerActive(false);
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    /**
     * Handle successful QR code scan
     * @param decodedText - The decoded text from the QR code
     */
    const handleScanSuccess = (decodedText: string) => {
        setScanResult(decodedText);
        setIsValidating(true);
        setError(null);

        // Validate the URL
        const validationResult = urlValidationService.validateUrl(decodedText);

        if (validationResult.isValid) {
            // Stop the scanner before navigating
            setIsScannerActive(false);

            // Navigate to the URL if it's valid
            handleValidUrl(validationResult);
        } else {
            // Show error if URL is invalid
            handleInvalidUrl(validationResult);
        }

        setIsValidating(false);
    };

    /**
     * Handle valid URL
     * @param validationResult - The validation result
     */
    const handleValidUrl = (validationResult: ValidationResult) => {
        try {
            const url = new URL(validationResult.url);

            // Extract the path from the URL
            const path = url.pathname;

            // Navigate to the path
            navigate(path);
        } catch (error) {
            setError('Failed to navigate to the scanned URL');
        }
    };

    /**
     * Handle invalid URL
     * @param validationResult - The validation result
     */
    const handleInvalidUrl = (validationResult: ValidationResult) => {
        setError(validationResult.errorMessage || 'Invalid QR code');
    };

    /**
     * Handle scan error
     * @param errorMessage - The error message
     */
    const handleScanError = (errorMessage: string) => {
        console.error('Scan error:', errorMessage);
        setError(errorMessage);
        setIsScanning(false);
    };

    /**
     * Retry scanning after an error
     */
    const handleRetry = () => {
        setError(null);
        setIsScanning(true);
        setIsScannerActive(true);

        // Re-request camera permission if needed
        if (!cameraPermission) {
            navigator.mediaDevices.getUserMedia({ video: true })
                .then(() => {
                    setCameraPermission(true);
                })
                .catch((err) => {
                    console.error('Camera permission denied again:', err);
                    setCameraPermission(false);
                    setError('Camera access denied. Please allow camera access to scan QR codes.');
                });
        }
    };

    return (
        <div className="scan-page flex flex-col h-full gap-5">
            {/* Header */}
            <div className="p-4 bg-white dark:bg-neutral-800 shadow-sm z-10">
                <h1 className="text-2xl font-bold">Scan QR Code</h1>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Point your camera at a museum QR code
                </p>
            </div>

            {/* Scanner component - takes up most of the screen */}
            <div className="flex-grow relative flex flex-col gap-5">
                {/* Scanner overlay */}
                {isScanning && cameraPermission && (
                    <>
                        <QrScanner
                            onScanSuccess={handleScanSuccess}
                            onScanError={handleScanError}
                            width="100%"
                            height="100%"
                            showBorder={false}
                            showScanningIndicator={false}
                            isActive={isScannerActive}
                        />
                    </>
                )}

                {/* Camera permission denied message */}
                {cameraPermission === false && (
                    <div className="flex items-center justify-center bg-neutral-900 text-white p-6 text-center">
                        <div>
                            <svg className="w-16 h-16 mx-auto mb-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <h3 className="text-lg font-medium mb-2">Camera Access Required</h3>
                            <p className="mb-4">Please allow camera access to scan QR codes.</p>
                            <button
                                className="px-4 py-2 bg-blue-600 text-white rounded-md"
                                onClick={handleRetry}
                            >
                                Try Again
                            </button>
                        </div>
                    </div>
                )}

                {/* Loading indicator */}
                {cameraPermission === null && (
                    <div className="flex items-center justify-center bg-neutral-900 text-white">
                        <div className="text-center">
                            <div className="w-12 h-12 border-4 border-t-blue-500 border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin mx-auto mb-4"></div>
                            <p>Opening camera...</p>
                        </div>
                    </div>
                )}

                {/* Scanning indicator */}
                {isScanning && !error && cameraPermission && (
                    <div className="flex justify-center">
                        <div className="bg-black bg-opacity-50 text-white px-4 py-2 rounded-full flex items-center">
                            <div className="h-3 w-3 bg-green-500 rounded-full animate-pulse mr-2"></div>
                            <span>Scanning...</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Status messages */}
            <div className="p-4 bg-white dark:bg-neutral-800 shadow-lg">
                {/* Validation status */}
                {isValidating && (
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-md mb-2">
                        <div className="flex items-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 dark:border-blue-400 mr-2"></div>
                            <p className="text-blue-600 dark:text-blue-400">Validating QR code...</p>
                        </div>
                    </div>
                )}

                {/* Error message */}
                {error && (
                    <div className="p-3 bg-red-50 dark:bg-red-900/30 rounded-md mb-2">
                        <p className="text-red-600 dark:text-red-400 font-medium">Error: {error}</p>
                        <p className="text-sm text-red-500 dark:text-red-300 mt-1">
                            Please scan a valid museum QR code.
                        </p>
                        {cameraPermission !== false && (
                            <button
                                className="mt-2 px-3 py-1 bg-red-600 text-white text-xs rounded-md"
                                onClick={handleRetry}
                            >
                                Try Again
                            </button>
                        )}
                    </div>
                )}

                {/* Success message */}
                {scanResult && !error && !isValidating && (
                    <div className="p-3 bg-green-50 dark:bg-green-900/30 rounded-md mb-2">
                        <p className="text-green-600 dark:text-green-400 font-medium">Successfully scanned!</p>
                        <p className="text-sm text-green-500 dark:text-green-300 mt-1">Navigating to the content...</p>
                    </div>
                )}

                {/* Scanning tips */}
                <div className="mt-2">
                    <h2 className="text-sm font-medium mb-1">Scanning Tips:</h2>
                    <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                        <li>• Make sure the QR code is well-lit and clearly visible</li>
                        <li>• Hold your device steady while scanning</li>
                        <li>• Position the QR code within the scanning frame</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default ScanPage;
